import type { FunctionComponent } from "react";
import type { PreloadedQuery } from "react-relay";
import { graphql, useFragment, usePreloadedQuery } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { query } from "scmp-app/components/search/search-explore-topics";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { searchExploreTopicsListQueue$key } from "scmp-app/queries/__generated__/searchExploreTopicsListQueue.graphql";
import type { searchExploreTopicsQuery } from "scmp-app/queries/__generated__/searchExploreTopicsQuery.graphql";

import { StyledTopic, StyledTopicLink } from "./styles";

export type Props = {
  queryReference: PreloadedQuery<searchExploreTopicsQuery>;
};

export const SearchExploreTopicsList: FunctionComponent<Props> = ({ queryReference }) => {
  const data = usePreloadedQuery<searchExploreTopicsQuery>(query, queryReference);

  const fragment = useFragment<searchExploreTopicsListQueue$key>(
    graphql`
      fragment searchExploreTopicsListQueue on Queue {
        items(first: $first) {
          edges {
            node {
              __typename
              ... on Topic {
                entityId
                ...topicLinkTopic
              }
            }
          }
        }
      }
    `,
    data.queue,
  );

  const handleTopicClickTracking = (entityId: string) => {
    // GA4 21.4.1
    sendGA4Tracking({
      action: "click",
      category: "search",
      customized_parameters: {
        topic_id: entityId,
      },
      subcategory: "suggest_topics",
    } as SearchEvent);
  };

  return (
    <div>
      {fragment?.items?.edges.filter(notEmpty).map(({ node }) => {
        switch (node.__typename) {
          case "Topic":
            return (
              <StyledTopic key={node.entityId}>
                <StyledTopicLink
                  onClick={() => handleTopicClickTracking(node.entityId)}
                  reference={node}
                />
              </StyledTopic>
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

SearchExploreTopicsList.displayName = "SearchExploreTopicsList";
